import React, { useState } from 'react';
import { Calendar, Clock, Trash2, Plus, Save, Edit2, Check, X, ThermometerSun, Users, Droplet } from 'lucide-react';

interface CleaningTask {
  id: string;
  zone: string;
  frequency: string;
  lastCleaned: string;
  nextScheduled: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'completed' | 'overdue';
}

interface HVACSchedule {
  id: string;
  zone: string;
  startTime: string;
  endTime: string;
  targetTemp: number;
  occupancyThreshold: number;
  mode: 'cooling' | 'heating' | 'auto';
  isActive: boolean;
}

interface RoomOptimization {
  id: string;
  name: string;
  capacity: number;
  averageAttendees: number;
  utilizationRate: number;
  peakHours: string;
  recommendations: string[];
}

const ResourceScheduler: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'cleaning' | 'hvac' | 'rooms'>('cleaning');
  const [cleaningTasks, setCleaningTasks] = useState<CleaningTask[]>([
    {
      id: '1',
      zone: 'Conference Room A',
      frequency: 'Daily',
      lastCleaned: '2024-03-20 08:00',
      nextScheduled: '2024-03-21 08:00',
      priority: 'high',
      status: 'completed'
    }
  ]);
  const [hvacSchedules, setHvacSchedules] = useState<HVACSchedule[]>([
    {
      id: '1',
      zone: 'Main Office',
      startTime: '08:00',
      endTime: '18:00',
      targetTemp: 72,
      occupancyThreshold: 10,
      mode: 'auto',
      isActive: true
    }
  ]);
  const [roomOptimizations, setRoomOptimizations] = useState<RoomOptimization[]>([
    {
      id: '1',
      name: 'Conference Room A',
      capacity: 12,
      averageAttendees: 6,
      utilizationRate: 65,
      peakHours: '10:00-14:00',
      recommendations: ['Consider splitting into two smaller rooms']
    }
  ]);
  
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAddItem = () => {
    setShowAddForm(true);
  };

  const handleEditItem = (id: string) => {
    setEditingItem(id);
  };

  const handleDeleteItem = (id: string) => {
    switch (activeTab) {
      case 'cleaning':
        setCleaningTasks(tasks => tasks.filter(task => task.id !== id));
        break;
      case 'hvac':
        setHvacSchedules(schedules => schedules.filter(schedule => schedule.id !== id));
        break;
      case 'rooms':
        setRoomOptimizations(rooms => rooms.filter(room => room.id !== id));
        break;
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Resource Scheduler</h1>
      
      <div className="flex space-x-4 mb-6">
        <button
          className={`px-4 py-2 rounded ${activeTab === 'cleaning' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('cleaning')}
        >
          Cleaning Schedule
        </button>
        <button
          className={`px-4 py-2 rounded ${activeTab === 'hvac' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('hvac')}
        >
          HVAC Management
        </button>
        <button
          className={`px-4 py-2 rounded ${activeTab === 'rooms' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('rooms')}
        >
          Room Optimization
        </button>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          {activeTab === 'cleaning' && 'Cleaning Tasks'}
          {activeTab === 'hvac' && 'HVAC Schedules'}
          {activeTab === 'rooms' && 'Room Optimizations'}
        </h2>
        <button
          className="flex items-center px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          onClick={handleAddItem}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New
        </button>
      </div>

      <div className="grid gap-4">
        {activeTab === 'cleaning' && cleaningTasks.map(task => (
          <div key={task.id} className="p-4 bg-white rounded-lg shadow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{task.zone}</h3>
                <p className="text-sm text-gray-600">Frequency: {task.frequency}</p>
                <p className="text-sm text-gray-600">Last Cleaned: {task.lastCleaned}</p>
                <p className="text-sm text-gray-600">Next Scheduled: {task.nextScheduled}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleEditItem(task.id)}>
                  <Edit2 className="w-4 h-4 text-blue-500" />
                </button>
                <button onClick={() => handleDeleteItem(task.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {activeTab === 'hvac' && hvacSchedules.map(schedule => (
          <div key={schedule.id} className="p-4 bg-white rounded-lg shadow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{schedule.zone}</h3>
                <p className="text-sm text-gray-600">Hours: {schedule.startTime} - {schedule.endTime}</p>
                <p className="text-sm text-gray-600">Target Temperature: {schedule.targetTemp}°F</p>
                <p className="text-sm text-gray-600">Mode: {schedule.mode}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleEditItem(schedule.id)}>
                  <Edit2 className="w-4 h-4 text-blue-500" />
                </button>
                <button onClick={() => handleDeleteItem(schedule.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {activeTab === 'rooms' && roomOptimizations.map(room => (
          <div key={room.id} className="p-4 bg-white rounded-lg shadow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{room.name}</h3>
                <p className="text-sm text-gray-600">Capacity: {room.capacity}</p>
                <p className="text-sm text-gray-600">Average Attendees: {room.averageAttendees}</p>
                <p className="text-sm text-gray-600">Utilization Rate: {room.utilizationRate}%</p>
                <p className="text-sm text-gray-600">Peak Hours: {room.peakHours}</p>
                <div className="mt-2">
                  <p className="text-sm font-medium">Recommendations:</p>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {room.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleEditItem(room.id)}>
                  <Edit2 className="w-4 h-4 text-blue-500" />
                </button>
                <button onClick={() => handleDeleteItem(room.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResourceScheduler; 